import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Label } from '@mercai/clever'

import { VariableMapperProps } from './variable-mapper.type'

import styles from './variable-mapper.module.css'

export const VariableMapper = ({
  selectedMetaTemplate,
  onMappingChange,
  availableVariables,
}: VariableMapperProps) => {
  const { t } = useTranslation('screenAiCenter')

  const [mapping, setMapping] = useState<Record<string, string>>({})
  const [dragOverTarget, setDragOverTarget] = useState<string | null>(null)

  const templateParams =
    selectedMetaTemplate?.components?.[0]?.example?.bodyTextNamedParams || []

  const handleDragStart = (e: React.DragEvent, variableId: string) => {
    e.dataTransfer.setData('variableId', variableId)
    e.dataTransfer.effectAllowed = 'copy'
  }

  const handleDragOver = (e: React.DragEvent, paramName: string) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'copy'
    setDragOverTarget(paramName)
  }

  const handleDragLeave = () => {
    setDragOverTarget(null)
  }

  const handleDrop = (e: React.DragEvent, paramName: string) => {
    e.preventDefault()
    const variableId = e.dataTransfer.getData('variableId')
    const newMapping = { ...mapping, [paramName]: variableId }
    setMapping(newMapping)
    onMappingChange(newMapping)
    setDragOverTarget(null)
  }

  const handleClear = (paramName: string) => {
    const newMapping = { ...mapping }
    delete newMapping[paramName]
    setMapping(newMapping)
    onMappingChange(newMapping)
  }

  const getVariableLabel = (variableId: string) => {
    return availableVariables.find((v) => v === variableId) || variableId
  }

  if (!selectedMetaTemplate || templateParams.length === 0) {
    return null
  }

  return (
    <div className={styles.container}>
      <div className={styles['available-variables']}>
        <Label>
          {t('conversationNewTemplateVariableMapperAvailableVariables')}
        </Label>

        <div className={styles['variables-list']}>
          {availableVariables?.map((variable) => (
            <div
              key={variable}
              className={styles['draggable-variable']}
              draggable
              onDragStart={(e) => handleDragStart(e, variable)}
            >
              {variable}
            </div>
          ))}
        </div>
      </div>

      <div className={styles['mapping-section']}>
        <Label>
          {t('conversationNewTemplateVariableMapperMappingSectionTitle')}
        </Label>

        <div className={styles['mapping-section-options']}>
          {templateParams.map((param) => (
            <div key={param.paramName} className={styles['mapping-row']}>
              <div className={styles['param-name']}>{param.paramName}</div>

              <div
                className={`${styles['drop-zone']} ${
                  dragOverTarget === param.paramName ? styles['drag-over'] : ''
                } ${mapping[param.paramName] ? styles.filled : ''}`}
                onDragOver={(e) => handleDragOver(e, param.paramName)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, param.paramName)}
              >
                {mapping[param.paramName] ? (
                  <>
                    <span>{getVariableLabel(mapping[param.paramName])}</span>

                    <button
                      className={styles['clear-button']}
                      onClick={() => handleClear(param.paramName)}
                    >
                      ✕
                    </button>
                  </>
                ) : (
                  t('conversationNewTemplateVariableMapperDropZone')
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
